import React from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import format from 'string-template';
import { Tooltip } from '@mui/material';

import EventOptionsMenu from '../EventOptionsMenu';
import { LocationType } from '../../../../../../../Model/Track';
import { isLongSessionWorkout } from '../../../../../../../utils/longSession';

import {
  Container,
  TitleColumn,
  TitleRow,
  LocationTag,
  ProductLabel,
  Title,
  StyledIconButton,
  StyledCheckCircleOutline,
  StyledRadioButtonUnchecked,
} from './styles';
import texts from './texts.json';

// Set on assignments from a challenge that runs on a home and a gym program; absent on every other one.
const locationTags = {
  [LocationType.GYM]: texts.locationTags.gym,
  [LocationType.HOME]: texts.locationTags.home,
};

const EventContent = ({
  workoutAssignment,
  showProductName,
  onWorkoutAssignmentClick,
  editWorkoutAssignment,
  deleteWorkoutAssignment,
  copyWorkoutAssignment,
  onMarkAsDone,
}) => {
  const {
    startDate,
    isCompleted,
    workoutStartedDate,
    productName,
    location,
  } = workoutAssignment;

  const isPastWorkout = moment(startDate.toDate()).isSameOrBefore(moment(), 'day');
  const isPendingCompletion = !!workoutStartedDate && !isCompleted;
  const labelledProduct = showProductName && productName ? productName : undefined;
  const locationTag = locationTags[location];
  /*
    A long session's content is a single server-written video, so an assignment has nothing to edit
    beyond name and note — and the editor is built around the workout document, which an assignment
    does not carry. Offering Edit would open a panel that can only start a new, unrelated upload.
  */
  const canEdit = !isLongSessionWorkout(workoutAssignment.workoutContent);

  return (
    <Container>
      {isPastWorkout && (
        isCompleted ? <StyledCheckCircleOutline /> : <StyledRadioButtonUnchecked />
      )}
      <Tooltip
        title={labelledProduct ? (
          <>
            {workoutAssignment.name}
            <br />
            {labelledProduct}
          </>
        ) : workoutAssignment.name}
        arrow
      >
        <TitleColumn onClick={() => onWorkoutAssignmentClick(workoutAssignment)} className="drag">
          <TitleRow>
            <Title>{workoutAssignment.name}</Title>
            {!!locationTag && (
              <LocationTag role="img" aria-label={locationTag.name}>{locationTag.letter}</LocationTag>
            )}
          </TitleRow>
          {!!labelledProduct && <ProductLabel>{labelledProduct}</ProductLabel>}
        </TitleColumn>
      </Tooltip>
      <EventOptionsMenu
        TriggerButton={StyledIconButton}
        ariaLabel={format(texts.optionsLabel, { name: workoutAssignment.name })}
        options={[
          ...(canEdit
            ? [{
              label: texts.edit,
              onClick: (close) => editWorkoutAssignment(workoutAssignment, close),
            }]
            : []),
          {
            label: texts.delete,
            onClick: (close) => deleteWorkoutAssignment(workoutAssignment, close),
          },
          {
            label: texts.copy,
            divider: isPendingCompletion,
            onClick: (close) => copyWorkoutAssignment(
              {
                id: workoutAssignment.workout,
                name: workoutAssignment.workoutContent.name,
                data: workoutAssignment.workoutContent,
                // Carried so a paste keeps the product it was copied from.
                productId: workoutAssignment.productId,
                productName: workoutAssignment.productName,
              },
              close,
            ),
          },
          ...(isPendingCompletion
            ? [{
              label: texts.complete,
              onClick: (close) => onMarkAsDone(workoutAssignment, close),
            }]
            : []),
        ]}
      />
    </Container>
  );
};

EventContent.propTypes = {
  workoutAssignment: PropTypes.object.isRequired,
  onWorkoutAssignmentClick: PropTypes.func.isRequired,
  editWorkoutAssignment: PropTypes.func.isRequired,
  deleteWorkoutAssignment: PropTypes.func.isRequired,
  copyWorkoutAssignment: PropTypes.func.isRequired,
  showProductName: PropTypes.bool,
  onMarkAsDone: PropTypes.func.isRequired,
};

EventContent.defaultProps = {
  showProductName: false,
};

export default EventContent;
